<template>
    <USlideover prevent-close :model-value="open" @update:model-value="$emit('update:open', $event)" :ui="{ width: 'max-w-3xl' }">
        <UCard>
            <template #header>
                <div class="flex items-center justify-between">
                    <h3 class="text-base font-medium leading-6 text-gray-800">Create Invoice</h3>
                </div>
            </template>

            <div class="space-y-6">
                <UFormGroup name="partner_id" label="Partner" required>
                    <UInputMenu
                        v-model="partner.selected"
                        @update:model-value="state.partner_id = $event.id"
                        :loading="partner.searching"
                        :search="searchPartner"
                        placeholder="Choose a partner..."
                        option-attribute="name"
                        size="xl"
                        trailing
                        by="id"
                    />
                </UFormGroup>
                <template v-if="partner.selected">
                    <UCheckbox v-model="state.onlySignedInCustomers" label="Only signed-in customers" />
                    <div v-if="loading" class="flex items-center justify-center space-x-2 p-6 rounded-md border border-dashed border-strong border-gray-300">
                        <UIcon name="i-ph-circle-dashed" class="size-4 text-gray-700 animate-spin-slow" />
                        <span class="text-sm text-gray-500">Loading uninvoiced subscriptions...</span>
                    </div>
                    <div v-else-if="!state.subscriptions.length" class="flex flex-col items-center justify-center space-y-2 p-6 rounded-md border border-dashed border-strong border-gray-300">
                        <UIcon name="i-ph-shield-duotone" class="size-6 text-gray-300" />
                        <span class="text-sm text-gray-400 italic">No uninvoiced subscriptions.</span>
                    </div>
                    <div v-else class="space-y-6">
                        <UFormGroup name="unit_price" label="Unit Price" required>
                            <UInput v-model="state.unit_price" type="number" size="xl">
                                <template #leading>$</template>
                            </UInput>
                        </UFormGroup>
                        <div class="space-y-2">
                            <p class="text-sm font-medium">Subscriptions</p>
                            <UTable class="border rounded-md min-h-[300px]" :rows="paginatedSubscriptions" :columns="tableColumns">
                                <template #id-data="{ row }">
                                    <p class="text-sm">
                                        {{ row.id.split('-').at(-1)?.toUpperCase() }}
                                    </p>
                                </template>
                                <template #customer-data="{ row }">
                                    <p class="text-sm">
                                        {{ [row.customer.first_name, row.customer.middle_name, row.customer.last_name].filter(Boolean).join(' ') }}
                                    </p>
                                </template>
                                <template #starts-data="{ row }">
                                    <TableDateCell :date="row.created_at" without-time class="text-sm" />
                                </template>
                                <template #expiry-data="{ row }">
                                    <TableDateCell :date="row.expires_at" without-time class="text-sm" />
                                </template>
                            </UTable>

                            <TablePagination :pagination="pagination" class="shrink-0" />
                        </div>
                    </div>
                </template>
            </div>

            <template #footer>
                <div class="flex items-center justify-between space-x-2">
                    <div class="flex items-center justify-start space-x-2">
                        <Field v-if="partner.selected" label="Subtotal" class="leading-4">
                            <span class="font-mono">
                                ${{ ((invoiceTotal || 0) / 100).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                            </span>
                        </Field>
                    </div>
                    <div class="flex items-center justify-end space-x-2">
                        <UButton @click="dismiss()" size="lg" color="white">
                            Cancel
                        </UButton>
                        <UButton @click="confirm()" size="lg" :loading="confirming" :disabled="!canCreate" color="black">
                            Create
                        </UButton>
                    </div>
                </div>
            </template>
        </UCard>
    </USlideover>
</template>

<script setup lang="ts">
import dayjs from 'dayjs';
import type { Partner } from '~/types/partner'
import { SubscriptionSourceType, type Subscription } from '~/types/subscription';

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [boolean]
}>()

const props = defineProps<{
    open: boolean
}>()

const toast = useToast()
const client = useSupabaseClient()
const pagination = usePagination(20)

const loading = ref<boolean>(false)
const confirming = ref<boolean>(false)

const partner = ref<{
    searching: boolean
    selected?: Partial<Partner>
}>({
    searching: false,
})

const state = ref<{
    partner_id?: string
    subscriptions: Subscription[]
    unit_price: number
    onlySignedInCustomers: boolean
}>({
    partner_id: undefined,
    subscriptions: [],
    unit_price: 0,
    onlySignedInCustomers: false,
})

const tableColumns = [
    { key: 'id', label: 'ID' },
    { key: 'customer', label: 'Customer' },
    { key: 'starts', label: 'Start Date' },
    { key: 'expiry', label: 'Expiry Date' },
]

const canCreate = computed(() => {
    return !!state.value.partner_id && !!state.value.subscriptions.length
})

const paginatedSubscriptions = computed(() => {
    const start = pagination.page * pagination.limit
    const end = (pagination.page + 1) * pagination.limit
    return state.value.subscriptions.slice(start, end)
})

const invoiceTotal = computed(() => state.value.subscriptions.reduce((sum, s) => {
    const createdAt = dayjs(s.created_at)
    const expiresAt = dayjs(s.expires_at)

    const years = expiresAt.diff(createdAt, 'year')
    const months = expiresAt.diff(createdAt, 'month') % 12
    const remainingDays = expiresAt.diff(
        createdAt.add(years, 'year').add(months, 'month'),
        'day'
    )

    const totalMonths = Math.max(1, years * 12 + months + (remainingDays > 0 ? 1 : 0))
    return sum + Math.round((state.value.unit_price / 12) * totalMonths * 100)
}, 0))

watch(() => props.open, (open) => {
    if (open) {
        reset()
    }
})

watch(() => state.value.partner_id, () => {
    fetchSubscriptions()
})

watch(() => state.value.onlySignedInCustomers, () => {
    fetchSubscriptions()
})

async function searchPartner(q: string) {
    partner.value.searching = true

    const { data, error } = await client
        .from('partners')
        .select('id, name, metadata')
        .is('deleted_at', null)
        .eq('metadata ->> is_public', true)
        .ilike('name', `%${q}%`)

    if (error) {
        return []
    }

    partner.value.searching = false
    return data
}

async function fetchSubscriptions() {
    loading.value = true

    if (state.value.partner_id) {
        const { data } = await client.rpc('get_uninvoiced_subscriptions', {
            p_partner_id: state.value.partner_id,
            p_only_signed_in: state.value.onlySignedInCustomers
        } as never)

        if (data) {
            const subscriptions = data as Subscription[]
            state.value.subscriptions = subscriptions
            pagination.setTotal(subscriptions.length)
        }
    }
    else {
        state.value.subscriptions = []
        pagination.setTotal(undefined)
    }

    loading.value = false
}

async function confirm() {
    if (state.value.partner_id) {
        confirming.value = true

        const { data: lastInvoice } = await client.from('invoices')
            .select('number')
            .eq('partner_id', state.value.partner_id)
            .order('number', { ascending: false })
            .limit(1)
            .maybeSingle()

        const nextInvoiceNumber = lastInvoice?.number ? Number.parseInt(lastInvoice.number) + 1 : 1

        const { data: invoice, error } = await client.from('invoices')
            .insert({
                number: nextInvoiceNumber,
                partner_id: state.value.partner_id,
                unit_price: state.value.unit_price * 100,
            } as never)
            .select('id')
            .single()

        if (!invoice || error) {
            toast.add({
                title: 'Could not create invoice',
                description: 'Please try again later.',
                color: 'red',
                icon: 'i-ph-warning-duotone'
            })

            dismiss()
        }
        else {
            await client.from('subscriptions')
                .update({ invoice_id: invoice.id } as never)
                .eq('partner_id', state.value.partner_id)
                .eq('source_type', SubscriptionSourceType.credential)
                .is('invoice_id', null)

            dismiss(true)
        }
    }
}

function dismiss(reload: boolean = false) {
    emit('update:open', false)

    if (reload) {
        emit('reload', true)
    }
}

function reset() {
    loading.value = false
    confirming.value = false
    pagination.setTotal(undefined)
    pagination.page = 0

    partner.value = {
        searching: false,
        selected: undefined
    }

    state.value = {
        partner_id: undefined,
        subscriptions: [],
        unit_price: 0,
        onlySignedInCustomers: false,
    }
}
</script>
